
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import Head from 'next/head';
import useTranslation from 'next-translate/useTranslation';
import RelatedLinks from '../components/RelatedLinks';
import { getFlightData } from '../lib/flightsFetcher';
import { SITE_URL } from '../lib/site';
// The official CPH overview map. Linked, not embedded: it is their artwork.
const CPH_MAP_PDF = 'https://www.cph.dk/490722/globalassets/3.-praktisk/oversigtskort/oversigtskort-kobenhavns-lufthavn-januar-2024.pdf';
// Which terminal does each airline depart from? Derived from the live
// departure board rather than kept as a hand-written list, because a static
// list goes stale silently and this one cannot: it is the same feed the
// departures page already uses, so there is no extra request to CPH.
function airlineTerminals(flights) {
    const byAirline = new Map();
    for (const f of flights || []) {
        const airline = f.Airline || f.IataAirline;
        const terminal = f.Terminal;
        if (!airline || !terminal)
            continue;
        if (!byAirline.has(airline))
            byAirline.set(airline, new Map());
        const counts = byAirline.get(airline);
        counts.set(terminal, (counts.get(terminal) || 0) + 1);
    }
    return [...byAirline.entries()]
        .map(([airline, counts]) => {
        const total = [...counts.values()].reduce((a, b) => a + b, 0);
        // An airline can use both. Keep every terminal it actually flies
        // from, busiest first, instead of flattening to one and lying.
        const terminals = [...counts.entries()]
            .sort((a, b) => b[1] - a[1])
            .map(([t]) => t.replace(/[^0-9]/g, ''))
            .filter(Boolean);
        return { airline, terminals, total };
    })
        .filter((r) => r.terminals.length)
        .sort((a, b) => b.total - a.total || a.airline.localeCompare(b.airline));
}
async function getStaticProps() {
    try {
        const flights = await getFlightData({ direction: 'D' });
        return {
            props: { airlines: airlineTerminals(flights) },
            // The airline-to-terminal mapping barely moves. Hourly is plenty.
            revalidate: 3600
        };
    }
    catch (error) {
        console.error('Error in getStaticProps for TerminalsPage:', error);
        return { props: { airlines: [] }, revalidate: 600 };
    }
}
function TerminalsPage({ airlines }) {
    const { t, lang } = useTranslation('common');
    const url = lang === 'en' ? `${SITE_URL}/en/terminals` : `${SITE_URL}/terminals`;
    return (<div className="bg-white p-4 m-4 sm:rounded">
            {/* Static and localized. No live numbers in meta: a baked-in
            figure reads stale in the SERP (see PR #33). */}
            <Head>
                <title>{t('terminals_meta_title')}</title>
                <meta name="description" content={t('terminals_meta_description')}/>
                <meta name="robots" content="all"/>
                <link rel="canonical" href={url}/>
                <meta property="og:title" content={t('terminals_meta_title')}/>
                <meta property="og:description" content={t('terminals_meta_description')}/>
                <meta property="og:url" content={url}/>
            </Head>
            <h1 className="text-xl">{t('terminals_h1')}</h1>

            <p className="my-4">{t('terminals_intro')}</p>

            <div className="my-6 rounded bg-slate-50 border p-4">
                <p className="font-medium mb-1">{t('terminals_one_security')}</p>
                <p className="text-sm text-gray-600 mb-0">
                    {t('terminals_one_security_detail')}
                </p>
            </div>

            <h2 className="text-lg mt-8">{t('terminals_which_heading')}</h2>
            {airlines.length ? (<>
                    <p className="my-3 text-sm text-gray-600">
                        {t('terminals_table_note')}
                    </p>
                    <div className="overflow-x-auto">
                        <table className="border-collapse w-full md:w-auto md:min-w-96 text-left rounded-xl">
                            <thead>
                                <tr className="*:border-2 *:p-3 bg-slate-100">
                                    <th>{t('terminals_airline')}</th>
                                    <th>{t('terminals_terminal')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {airlines.map((row) => (<tr key={row.airline} className="*:border-2 *:p-3 even:bg-slate-50">
                                        <td>{row.airline}</td>
                                        <td className="whitespace-nowrap font-medium">
                                            {row.terminals.join(' + ')}
                                        </td>
                                    </tr>))}
                            </tbody>
                        </table>
                    </div>
                </>) : (<p className="my-3">{t('terminals_unavailable')}</p>)}

            <h2 className="text-lg mt-8">{t('terminals_security_heading')}</h2>
            <p className="my-3">{t('terminals_security_body')}</p>
            <ul className="list-disc pl-6 my-3">
                <li>{t('terminals_security_electronics')}</li>
                <li>{t('terminals_security_liquids')}</li>
            </ul>

            <p className="my-6 text-sm">
                <a className="underline" href={CPH_MAP_PDF} target="_blank" rel="noopener noreferrer">
                    {t('terminals_official_map')}
                </a>
            </p>

            <RelatedLinks links={[
            { href: '/when-to-arrive', labelKey: 'link_when_to_arrive' },
            { href: '/passport-control', labelKey: 'link_passport' },
            { href: '/parking', labelKey: 'link_parking' }
        ]}/>
        </div>);
}
export default TerminalsPage;

    async function __Next_Translate__getStaticProps__1a1723ec6d9__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/terminals',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a1723ec6d9__ as getStaticProps }
  